import {
	createAlbum,
	deleteAlbum,
	fetchAlbum,
	fetchAlbumMedia,
	fetchAlbumSamples,
	fetchAlbums,
	updateAlbum,
	updateCoverImage,
	uploadAlbum,
} from "@bodhistreams/api/src/albumApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Query keys factory for album queries
 */
export const albumQueryKeys = {
	all: ["albums"],
	lists: () => [...albumQueryKeys.all, "list"],
	filtered: (params) => [...albumQueryKeys.all, "filtered", params],
	details: () => [...albumQueryKeys.all, "detail"],
	detail: (id) => [...albumQueryKeys.details(), id],
	media: () => [...albumQueryKeys.all, "media"],
	mediaByAlbum: (albumId) => [...albumQueryKeys.media(), "album", albumId],
	samples: () => [...albumQueryKeys.all, "samples"],
};

/**
 * Hook to fetch albums with optional filters
 * @param {Object} params - Query parameters
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useAlbums = (params = {}, enabled = true) => {
	return useQuery({
		queryKey: albumQueryKeys.filtered(params),
		queryFn: async () => {
			return await fetchAlbums(params);
		},
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
	});
};

/**
 * Hook to fetch public album samples for homepage
 * @returns {Object} React Query result
 */
export const useAlbumSamples = () => {
	return useQuery({
		queryKey: albumQueryKeys.samples(),
		queryFn: async () => {
			return await fetchAlbumSamples();
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
	});
};

/**
 * Hook to fetch a specific album by ID
 * @param {string} albumId - Album ID
 * @param {boolean} includeMedia - Include media in response
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useAlbum = (albumId, includeMedia = false, enabled = true) => {
	return useQuery({
		queryKey: albumQueryKeys.detail(albumId),
		queryFn: async () => {
			return await fetchAlbum(albumId, includeMedia);
		},
		enabled: enabled && !!albumId,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
	});
};

/**
 * Hook to fetch media in an album
 * @param {string} albumId - Album ID
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useAlbumMedia = (albumId, enabled = true) => {
	return useQuery({
		queryKey: albumQueryKeys.mediaByAlbum(albumId),
		queryFn: async () => {
			return await fetchAlbumMedia(albumId);
		},
		enabled: enabled && !!albumId,
		staleTime: 2 * 60 * 1000, // 2 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
	});
};

/**
 * Hook to create a new album
 * @returns {Object} React Query mutation result
 */
export const useCreateAlbum = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createAlbum,
		onSuccess: (newAlbum) => {
			// Invalidate and refetch albums
			queryClient.invalidateQueries({
				queryKey: albumQueryKeys.all,
			});
			// Add to cache
			queryClient.setQueryData(albumQueryKeys.detail(newAlbum.id), newAlbum);
		},
	});
};

/**
 * Hook to upload a new album with cover image
 * @returns {Object} React Query mutation result
 */
export const useUploadAlbum = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ albumData, coverImageFile, backgroundImageFile }) =>
			uploadAlbum(albumData, coverImageFile, backgroundImageFile),
		onSuccess: (newAlbum) => {
			// Invalidate and refetch albums
			queryClient.invalidateQueries({
				queryKey: albumQueryKeys.all,
			});
			// Add to cache
			queryClient.setQueryData(albumQueryKeys.detail(newAlbum.id), newAlbum);
		},
	});
};

/**
 * Hook to update an album
 * @returns {Object} React Query mutation result
 */
export const useUpdateAlbum = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ albumId, albumData, coverImageFile, backgroundImageFile }) =>
			updateAlbum(albumId, albumData, coverImageFile, backgroundImageFile),
		onSuccess: (updatedAlbum, { albumId }) => {
			// Update the specific album cache
			queryClient.setQueryData(albumQueryKeys.detail(albumId), updatedAlbum);
			// Invalidate album lists to reflect changes
			queryClient.invalidateQueries({
				queryKey: albumQueryKeys.all,
			});
		},
	});
};

/**
 * Hook to delete an album
 * @returns {Object} React Query mutation result
 */
export const useDeleteAlbum = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteAlbum,
		onSuccess: (_, albumId) => {
			// Remove from cache
			queryClient.removeQueries({
				queryKey: albumQueryKeys.detail(albumId),
			});
			// Invalidate album lists
			queryClient.invalidateQueries({
				queryKey: albumQueryKeys.all,
			});
		},
	});
};

/**
 * Hook to update album cover image
 * @returns {Object} React Query mutation result
 */
export const useUpdateCoverImage = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ albumId, imageUrl }) => updateCoverImage(albumId, imageUrl),
		onSuccess: (updatedAlbum, { albumId }) => {
			// Update the specific album cache
			queryClient.setQueryData(albumQueryKeys.detail(albumId), updatedAlbum);
			// Invalidate album lists to reflect changes
			queryClient.invalidateQueries({
				queryKey: albumQueryKeys.all,
			});
		},
	});
};

/**
 * Hook to get the query client for manual cache operations
 * @returns {Object} React Query client
 */
export const useAlbumQueryClient = () => {
	return useQueryClient();
};

/**
 * Hook to invalidate album queries
 * @returns {Object} Invalidation functions
 */
export const useInvalidateAlbums = () => {
	const queryClient = useQueryClient();

	return {
		invalidateAll: () =>
			queryClient.invalidateQueries({
				queryKey: albumQueryKeys.all,
			}),
		invalidateAlbum: (albumId) => {
			queryClient.invalidateQueries({
				queryKey: albumQueryKeys.detail(albumId),
			});
		},
		invalidateAlbumMedia: (albumId) => {
			queryClient.invalidateQueries({
				queryKey: albumQueryKeys.mediaByAlbum(albumId),
			});
		},
	};
};
